import React, { useState } from 'react';
import api from '../services/api';
import {
  FiDownloadCloud,
  FiCalendar,
  FiBarChart2,
  FiUsers,
  FiHome,
  FiMessageSquare,
  FiAward,
  FiFileText,
  FiCheckCircle,
  FiAlertCircle,
} from 'react-icons/fi';

const MONTHS = [
  { value: 1,  label: 'January' },
  { value: 2,  label: 'February' },
  { value: 3,  label: 'March' },
  { value: 4,  label: 'April' },
  { value: 5,  label: 'May' },
  { value: 6,  label: 'June' },
  { value: 7,  label: 'July' },
  { value: 8,  label: 'August' },
  { value: 9,  label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

const REPORT_TYPES = [
  {
    id: 'service_requests',
    title: 'Service Requests & Operations',
    shortTitle: 'Service Requests',
    description: 'Chronological request log, category distribution, resolution speed, and status breakdown.',
    icon: FiBarChart2,
    accentColor: '#2563EB',
    bgColor: '#EFF6FF',
    borderColor: '#BFDBFE',
    textColor: '#1D4ED8',
  },
  {
    id: 'department',
    title: 'Department SLA & Performance',
    shortTitle: 'Dept. Performance',
    description: 'Completion rates, avg response time by department, SLA benchmarking, and volume charts.',
    icon: FiAward,
    accentColor: '#7C3AED',
    bgColor: '#F5F3FF',
    borderColor: '#DDD6FE',
    textColor: '#6D28D9',
  },
  {
    id: 'feedback',
    title: 'Guest Feedback & Reviews',
    shortTitle: 'Feedback & Reviews',
    description: 'Star rating distribution, guest comments, staff Work Done remarks, and follow-up resolution.',
    icon: FiMessageSquare,
    accentColor: '#D97706',
    bgColor: '#FFFBEB',
    borderColor: '#FDE68A',
    textColor: '#B45309',
  },
  {
    id: 'rooms',
    title: 'Rooms & Stay Demand',
    shortTitle: 'Rooms',
    description: 'Room-wise operational load, demand levels, top requesting rooms, and completion rates.',
    icon: FiHome,
    accentColor: '#059669',
    bgColor: '#ECFDF5',
    borderColor: '#A7F3D0',
    textColor: '#047857',
  },
  {
    id: 'guests',
    title: 'Guest Master & Stays Log',
    shortTitle: 'Guest Log',
    description: 'Complete check-in / check-out register with phone, room, nationality, and stay status.',
    icon: FiUsers,
    accentColor: '#0891B2',
    bgColor: '#ECFEFF',
    borderColor: '#A5F3FC',
    textColor: '#0E7490',
  },
  {
    id: 'staff',
    title: 'Staff Roster & Dispatch Activity',
    shortTitle: 'Staff & Dispatch',
    description: 'Active staff directory, HOD assignments, department breakdown, and tasks handled per agent.',
    icon: FiFileText,
    accentColor: '#DC2626',
    bgColor: '#FEF2F2',
    borderColor: '#FECACA',
    textColor: '#B91C1C',
  },
];

function MonthlyReportExport() {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const defaultMonth = currentDate.getMonth() === 0 ? 12 : currentDate.getMonth();
  const defaultYear  = currentDate.getMonth() === 0 ? currentYear - 1 : currentYear;

  const [selectedMonth, setSelectedMonth] = useState(defaultMonth);
  const [selectedYear,  setSelectedYear]  = useState(defaultYear);
  const [downloading,   setDownloading]   = useState({}); // { report_id: true/false }
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [flash, setFlash] = useState({ type: '', text: '' });

  const monthName = MONTHS.find(m => m.value === Number(selectedMonth))?.label || '';

  const showFlash = (type, text) => {
    setFlash({ type, text });
    setTimeout(() => setFlash({ type: '', text: '' }), 5000);
  };

  // ── Download a single report ──────────────────────────
  const downloadSingle = async (reportId) => {
    try {
      setDownloading(prev => ({ ...prev, [reportId]: true }));
      const report = REPORT_TYPES.find(r => r.id === reportId);
      const fileName = `Oleander_${report.shortTitle.replace(/\s+/g,'_')}_${monthName}_${selectedYear}.pdf`;

      const response = await api.post(
        '/api/admin/reports/monthly-pdf',
        { month: selectedMonth, year: selectedYear, report_type: reportId },
        { responseType: 'blob' }
      );

      triggerDownload(response.data, fileName);
      showFlash('success', `"${report.title}" PDF for ${monthName} ${selectedYear} downloaded.`);
    } catch (error) {
      console.error('Download failed:', error);
      showFlash('error', `Failed to download report. Please try again.`);
    } finally {
      setDownloading(prev => ({ ...prev, [reportId]: false }));
    }
  };

  // ── Download all as one combined PDF ────────────────
  const downloadAll = async () => {
    try {
      setDownloadingAll(true);
      const fileName = `Oleander_Full_Executive_Report_${monthName}_${selectedYear}.pdf`;

      const response = await api.post(
        '/api/admin/reports/monthly-pdf',
        { month: selectedMonth, year: selectedYear, report_types: REPORT_TYPES.map(r => r.id) },
        { responseType: 'blob' }
      );

      triggerDownload(response.data, fileName);
      showFlash('success', `Complete executive report for ${monthName} ${selectedYear} downloaded!`);
    } catch (error) {
      console.error('Download all failed:', error);
      showFlash('error', 'Failed to download combined report. Please try again.');
    } finally {
      setDownloadingAll(false);
    }
  };

  const triggerDownload = (blob, filename) => {
    const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const anyDownloading = Object.values(downloading).some(Boolean) || downloadingAll;

  return (
    <div className="space-y-4">
      {/* ── Header ───────────────────────────────────── */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
              <div className="p-1.5 bg-gray-900 rounded-lg text-white">
                <FiDownloadCloud size={15} />
              </div>
              Monthly Executive PDF Reports
            </h2>
            <p className="text-xs text-gray-500 mt-1">
              Download individual PDF reports or the full combined executive report for any month.
              Each PDF includes visual graphs, KPI cards, and structured data tables.
            </p>
          </div>
        </div>

        {/* Flash message */}
        {flash.text && (
          <div className={`mt-3 flex items-center gap-2 px-3 py-2.5 rounded-lg border text-xs font-medium ${
            flash.type === 'success'
              ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
              : 'bg-rose-50 text-rose-800 border-rose-200'
          }`}>
            {flash.type === 'success'
              ? <FiCheckCircle className="flex-shrink-0 text-emerald-600" size={15} />
              : <FiAlertCircle className="flex-shrink-0 text-rose-600" size={15} />}
            {flash.text}
          </div>
        )}

        {/* ── Period selector ───────────────────────── */}
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-700">
            <FiCalendar size={13} className="text-gray-500" />
            Reporting Period:
          </div>

          <select
            value={selectedMonth}
            onChange={e => setSelectedMonth(Number(e.target.value))}
            className="px-3 py-1.5 bg-white border border-gray-300 rounded-lg text-sm font-semibold text-gray-800 focus:outline-none focus:ring-1 focus:ring-gray-900 cursor-pointer"
          >
            {MONTHS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>

          <select
            value={selectedYear}
            onChange={e => setSelectedYear(Number(e.target.value))}
            className="px-3 py-1.5 bg-white border border-gray-300 rounded-lg text-sm font-semibold text-gray-800 focus:outline-none focus:ring-1 focus:ring-gray-900 cursor-pointer"
          >
            {[2027, 2026, 2025, 2024].map(y => <option key={y} value={y}>{y}</option>)}
          </select>

          <div className="flex items-center gap-2">
            {[
              { month: 8, year: 2026, label: 'Aug 2026' },
              { month: 9, year: 2026, label: 'Sep 2026' },
            ].map(p => (
              <button
                key={p.label}
                type="button"
                onClick={() => { setSelectedMonth(p.month); setSelectedYear(p.year); }}
                className="text-xs px-2.5 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition cursor-pointer"
              >
                {p.label}
              </button>
            ))}
          </div>

          <span className="text-xs text-gray-400 ml-auto">
            Generating reports for: <span className="font-bold text-gray-800">{monthName} {selectedYear}</span>
          </span>
        </div>
      </div>

      {/* ── 6 Individual Report Cards ─────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {REPORT_TYPES.map(report => {
          const Icon = report.icon;
          const isLoading = !!downloading[report.id];

          return (
            <div
              key={report.id}
              className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm flex flex-col gap-3 hover:border-gray-300 transition"
            >
              {/* Card icon + title */}
              <div className="flex items-start gap-3">
                <div
                  className="p-2.5 rounded-xl flex-shrink-0"
                  style={{ backgroundColor: report.bgColor, border: `1px solid ${report.borderColor}` }}
                >
                  <Icon size={18} style={{ color: report.accentColor }} />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-bold text-gray-900 leading-tight">{report.title}</p>
                  <p className="text-[11px] text-gray-500 mt-0.5 leading-snug">{report.description}</p>
                </div>
              </div>

              {/* Download button */}
              <button
                type="button"
                onClick={() => downloadSingle(report.id)}
                disabled={anyDownloading}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs font-bold border transition disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                style={isLoading ? {
                  backgroundColor: report.bgColor,
                  borderColor: report.borderColor,
                  color: report.textColor,
                  opacity: 1
                } : {
                  backgroundColor: report.bgColor,
                  borderColor: report.borderColor,
                  color: report.textColor,
                }}
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-t-transparent" style={{ borderColor: report.accentColor, borderTopColor: 'transparent' }} />
                    <span>Generating {monthName} PDF…</span>
                  </>
                ) : (
                  <>
                    <FiDownloadCloud size={14} />
                    <span>Download {monthName} {selectedYear} PDF</span>
                  </>
                )}
              </button>
            </div>
          );
        })}
      </div>

      {/* ── Download All Combined ─────────────────── */}
      <div className="bg-gray-900 rounded-xl p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <p className="text-sm font-bold text-white">Full Combined Executive Report</p>
          <p className="text-xs text-gray-400 mt-0.5">
            All 6 report modules in a single branded PDF file &mdash; {monthName} {selectedYear}
          </p>
        </div>
        <button
          type="button"
          onClick={downloadAll}
          disabled={anyDownloading}
          className="bg-white hover:bg-gray-100 text-gray-900 px-5 py-2.5 rounded-xl text-xs font-bold flex items-center gap-2 transition disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer flex-shrink-0"
        >
          {downloadingAll ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-900 border-t-transparent" />
              <span>Generating Full Report…</span>
            </>
          ) : (
            <>
              <FiDownloadCloud size={16} />
              <span>Download Full Report ({monthName} {selectedYear})</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
}

export default MonthlyReportExport;
